import { memo, useState, useEffect } from 'react'
import type { Message } from '@/types/conversations'
import { workflowActivityStore } from '@/lib/workflow-activity-store'
import { formatStepId, getWorkflowTitle } from '@/lib/workflow-utils'

interface WorkflowProgressPanelProps {
  message: Message
  onAbort?: () => void
  onRetry?: () => void
  isAborting?: boolean
  isRetrying?: boolean
}

interface WorkflowStep {
  id: string
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  output?: string | null
  error?: string | null
  retry_count?: number
}

interface WorkflowData {
  name: string
  description: string
  steps: WorkflowStep[]
  failure_mode?: 'revert' | 'pause' | 'continue'
  status?: 'running' | 'paused' | 'reverted' | 'completed' | 'completed_with_failures'
  status_message?: string
  total_retry_count?: number
}

// StepActivity subscribes to activity store - only this re-renders on activity changes
function StepActivity({ stepId }: { stepId: string }) {
  const [activity, setActivity] = useState<string | null>(() =>
    workflowActivityStore.getActivity(stepId)
  )

  useEffect(() => {
    // Set initial value and subscribe to updates
    setActivity(workflowActivityStore.getActivity(stepId))
    return workflowActivityStore.subscribe(stepId, () => {
      setActivity(workflowActivityStore.getActivity(stepId))
    })
  }, [stepId])

  if (!activity) return null

  return (
    <p className="mt-1 text-xs text-blue-500 dark:text-blue-400 animate-pulse">
      {activity}
    </p>
  )
}

// Memoized step icon to prevent spinner restart when activity changes
const StepIcon = memo(function StepIcon({ status }: { status: WorkflowStep['status'] }) {
  if (status === 'completed') {
    return (
      <svg
        className="h-5 w-5 text-green-500"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22,4 12,14.01 9,11.01" />
      </svg>
    )
  }
  if (status === 'failed') {
    return (
      <svg
        className="h-5 w-5 text-red-500"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    )
  }
  if (status === 'in_progress') {
    return (
      <svg
        className="h-5 w-5 text-blue-500 animate-spin"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    )
  }
  // pending
  return <div className="h-5 w-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />
})

export function WorkflowProgressPanel({
  message,
  onAbort,
  onRetry,
  isAborting = false,
  isRetrying = false
}: WorkflowProgressPanelProps) {
  const workflowData = message.workflow_step_data as WorkflowData | null
  if (!workflowData?.steps) return null

  const formatStatusMessage = (statusMessage: string) => {
    return statusMessage.replace(/'([a-z_]+)'/g, (_, stepId) => `'${formatStepId(stepId)}'`)
  }

  // Find current in_progress step to determine if we're at commit
  const currentStep = workflowData.steps.find(s => s.status === 'in_progress')
  const isAtCommitStep = currentStep?.id?.includes('commit') ?? false

  // Determine workflow states
  const isMessageFailed = message.status === 'failed'  // Message-level failure (e.g., timeout)
  const isMessageInterrupted = message.status === 'interrupted'  // User aborted
  const isReverted = workflowData.status === 'reverted'
  const isPaused = workflowData.status === 'paused'
  const isCompletedWithFailures = workflowData.status === 'completed_with_failures'
  const isInterrupted = (workflowData.status as string) === 'interrupted'
  const isRunning = !isMessageFailed && !isMessageInterrupted && (workflowData.status === 'running' || (!workflowData.status && workflowData.steps.some(s => s.status === 'in_progress')))

  // Retry limit checks
  const totalRetryCount = workflowData.total_retry_count ?? 0
  const maxStepRetries = 3
  const maxTotalRetries = 5
  const hasStepAtLimit = workflowData.steps.some(
    s => s.status === 'failed' && (s.retry_count ?? 0) >= maxStepRetries
  )
  const isRetryLimitReached = hasStepAtLimit || totalRetryCount >= maxTotalRetries

  // Can retry when workflow is in a failed/stopped state
  const canRetry = (isMessageFailed || isMessageInterrupted || isReverted || isPaused || isCompletedWithFailures || isInterrupted) && onRetry

  // Format workflow name for display (fallback to description if name is an ID)
  const workflowTitle = getWorkflowTitle(workflowData.name, workflowData.description)

  return (
    <div className="mb-4 max-w-full overflow-hidden">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden w-full">
        {/* Header */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {workflowTitle}
          </h3>
          <div className="flex items-center gap-2">
            {/* Retry button - shown when workflow can be retried */}
            {canRetry && (
              isRetryLimitReached ? (
                <span className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Max retries reached
                </span>
              ) : (
                <button
                  onClick={onRetry}
                  disabled={isRetrying}
                  className="text-xs px-2 py-1 rounded flex items-center gap-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50"
                >
                  {isRetrying && (
                    <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {isRetrying ? 'Retrying...' : totalRetryCount > 0 ? `Retry (${totalRetryCount}/${maxTotalRetries})` : 'Retry'}
                </button>
              )
            )}
            {/* Abort button - only while workflow is running */}
            {onAbort && isRunning && (
              <button
                onClick={onAbort}
                disabled={isAtCommitStep || isAborting}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                  isAtCommitStep || isAborting
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50'
                }`}
              >
                {isAborting && (
                  <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {isAborting ? 'Aborting...' : 'Abort'}
              </button>
            )}
          </div>
        </div>

        {/* Status message - show when workflow is paused or reverted */}
        {workflowData.status_message && (isReverted || isPaused) && (
          <div className={`px-4 py-2 text-sm ${
            isReverted
              ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
              : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
          }`}>
            {formatStatusMessage(workflowData.status_message)}
          </div>
        )}

        {/* Steps */}
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {(() => {
            // Find the first in_progress step index (only one should spin at a time)
            const firstInProgressIndex = workflowData.steps.findIndex(s => s.status === 'in_progress')

            return workflowData.steps.map((step, index) => {
            // Compute effective status based on workflow and message state
            let effectiveStatus = step.status

            // Safeguard: only the FIRST in_progress step should show as in_progress
            // (handles race conditions where multiple steps might temporarily be in_progress)
            if (step.status === 'in_progress' && index !== firstInProgressIndex) {
              effectiveStatus = 'pending'
            }

            if (isMessageFailed && (step.status === 'in_progress' || step.status === 'pending')) {
              // Message failed (e.g., timeout) - show incomplete steps as failed
              effectiveStatus = 'failed'
            } else if ((isReverted || isPaused || isInterrupted || isMessageInterrupted) && step.status === 'in_progress') {
              // Workflow stopped - show in_progress step as failed (it was interrupted)
              effectiveStatus = 'failed'
            } else if ((isReverted || isPaused || isInterrupted || isMessageInterrupted) && step.status === 'pending') {
              // Workflow stopped - pending steps stay pending (they never ran)
              effectiveStatus = 'pending'
            }

            return (
              <div key={step.id} className="px-4 py-3">
                <div className="flex items-start gap-3">
                  {/* Status Icon - memoized to prevent spinner restart */}
                  <div className="mt-0.5 shrink-0">
                    <StepIcon status={effectiveStatus} />
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className={`text-sm font-medium ${
                      effectiveStatus === 'completed' ? 'text-gray-900 dark:text-gray-100' :
                      effectiveStatus === 'failed' ? 'text-red-600 dark:text-red-400' :
                      effectiveStatus === 'in_progress' ? 'text-blue-600 dark:text-blue-400' :
                      'text-gray-500 dark:text-gray-400'
                    }`}>
                      {formatStepId(step.id)}
                    </p>

                    {/* Current activity - show while in progress */}
                    {effectiveStatus === 'in_progress' && (
                      <StepActivity stepId={step.id} />
                    )}

                    {/* Output - show for both completed and failed (truncated, full output stays in DB) */}
                    {step.output && (effectiveStatus === 'completed' || effectiveStatus === 'failed') && (
                      <p className={`mt-1 text-xs [overflow-wrap:anywhere] ${
                        effectiveStatus === 'failed'
                          ? 'text-red-500 dark:text-red-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {step.output.length > 150 ? `${step.output.slice(0, 150)}…` : step.output}
                      </p>
                    )}

                    {/* Error - only for exception errors */}
                    {step.error && effectiveStatus === 'failed' && !step.output && (
                      <p className="mt-1 text-xs text-red-500 dark:text-red-400 [overflow-wrap:anywhere]">
                        {step.error}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )
          })
          })()}
        </div>
      </div>

    </div>
  )
}
